import React from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle, Minus } from "lucide-react";
import { RenderedHtml, htmlIsEmpty } from "modules/editor";
import { cn } from "lib/utils";
import {
  ReviewExplanation,
  ReviewFill,
  ReviewMatchRow,
  ReviewOption,
  type ReviewState,
} from "components/result/ReviewParts";
import type { EvaluatedSubQuestion } from "./passageEvaluation";
import { normalizeAnswer, asRecord } from "./passageEvaluation";

export interface PassageReviewSubQuestionCardProps {
  evaluated: EvaluatedSubQuestion;
  isHighlighted: boolean;
  onCardRef?: (el: HTMLDivElement | null) => void;
  /** Shown under the answer, e.g. the AI analysis on the learner's own result page. */
  footer?: React.ReactNode;
}

const includesId = (value: unknown, id: string) =>
  Array.isArray(value) ? value.map(String).includes(id) : String(value) === id;

/** One reviewed passage question. Answers use the same review parts as every other result. */
export const PassageReviewSubQuestionCard: React.FC<
  PassageReviewSubQuestionCardProps
> = ({ evaluated: eq, isHighlighted, onCardRef, footer }) => {
  const { t } = useTranslation();
  const sub = eq.question;
  const status: ReviewState = eq.isCorrect
    ? "correct"
    : eq.isAnswered
      ? "incorrect"
      : "empty";

  return (
    <div
      ref={onCardRef}
      aria-label={`${t("question")} ${eq.globalIndex}`}
      className={cn(
        "space-y-3 px-4 py-5 transition-colors duration-300 sm:px-5",
        isHighlighted && "bg-muted",
      )}
    >
      <div className="flex items-start gap-3">
        <span className="mt-px flex h-6 min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-semibold tabular-nums text-foreground">
          {eq.globalIndex}
        </span>
        <div className="min-w-0 flex-1 text-sm leading-relaxed text-foreground">
          <RenderedHtml html={sub.questionText || sub.prompt || ""} inline />
        </div>
        <SubQuestionStatus
          status={status}
          earned={eq.pointsEarned}
          max={eq.maxPoints}
        />
      </div>

      <div className="space-y-2 sm:pl-9">
        {["MULTIPLE_CHOICE", "MULTIPLE_RESPONSE", "TRUE_FALSE"].includes(
          sub.type,
        ) &&
          (sub.options || []).map((opt) => {
            const optId = String(opt.id);
            return (
              <ReviewOption
                key={optId}
                html={opt.value || opt.text || ""}
                isCorrect={includesId(eq.correctAnswer, optId)}
                isSelected={includesId(eq.userAnswer, optId)}
                multi={sub.type === "MULTIPLE_RESPONSE"}
              />
            );
          })}

        {["SHORT_ANSWER", "FILL_IN_BLANKS"].includes(sub.type) && (
          <ReviewFill
            state={status}
            given={
              eq.userAnswer && typeof eq.userAnswer === "object" ? (
                Object.values(eq.userAnswer).map(String).join(", ")
              ) : (
                <RenderedHtml html={String(eq.userAnswer ?? "")} inline />
              )
            }
            correct={
              eq.correctAnswer ? (
                <RenderedHtml html={String(eq.correctAnswer)} inline />
              ) : null
            }
          />
        )}

        {sub.type === "MATCHING" &&
          (sub.matchPairs || []).map((pair, pIdx) => {
            const userMap = asRecord(eq.userAnswer);
            const chosen = String(
              userMap[pair.key] ?? userMap[pair.key.trim()] ?? "",
            ).trim();
            return (
              <ReviewMatchRow
                key={pair.key || pIdx}
                state={
                  !chosen
                    ? "empty"
                    : normalizeAnswer(chosen) === normalizeAnswer(pair.value)
                      ? "correct"
                      : "incorrect"
                }
                label={<RenderedHtml html={pair.key} inline />}
                given={<RenderedHtml html={chosen} inline />}
                correct={<RenderedHtml html={pair.value || ""} inline />}
              />
            );
          })}
      </div>

      {sub.explanation && !htmlIsEmpty(sub.explanation) && (
        <ReviewExplanation html={sub.explanation} className="sm:ml-9" />
      )}
      {footer ? <div className="sm:pl-9">{footer}</div> : null}
    </div>
  );
};

function SubQuestionStatus({
  status,
  earned,
  max,
}: {
  status: ReviewState;
  earned: number;
  max: number;
}) {
  const { t } = useTranslation();
  const Icon =
    status === "correct" ? CheckCircle2 : status === "incorrect" ? XCircle : Minus;
  return (
    <span
      className={cn(
        "mt-0.5 flex shrink-0 items-center gap-1 text-xs font-medium",
        status === "correct" && "text-green-700 dark:text-green-400",
        status === "incorrect" && "text-red-600 dark:text-red-400",
        status === "empty" && "text-muted-foreground",
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {t(
        status === "correct"
          ? "correct"
          : status === "incorrect"
            ? "incorrect"
            : "not_answered",
      )}
      {max > 1 && (
        <span className="font-normal tabular-nums text-muted-foreground">
          · {earned}/{max}
        </span>
      )}
    </span>
  );
}
